import { getDefaultProjectStructure as getSharedDefaultProjectStructure } from '../../shared/projectFileSystem.ts';

// File system utilities

// Get file extension
export function getExtension(filename) {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

// Get file name without extension
export function getBaseName(filename) {
    const parts = filename.split('.');
    if (parts.length > 1) {
        parts.pop();
    }
    return parts.join('.');
}

// Get parent path from full path
export function getParentPath(path) {
    const parts = path.split('/');
    parts.pop();
    return parts.join('/');
}

// Get file name from path
export function getFileName(path) {
    return path.split('/').pop() || '';
}

// Check if path is a markdown file
export function isMarkdownFile(path) {
    const ext = getExtension(path);
    return ext === 'md' || ext === 'markdown';
}

// Check if path is a canvas file
export function isCanvasFile(path) {
    return getExtension(path) === 'canvas';
}

// Get file icon based on type/extension
export function getFileIcon(file) {
    if (file.type === 'folder') {
        return '📁';
    }

    const ext = getExtension(file.name);

    switch (ext) {
        case 'md':
        case 'markdown':
            return '📄';
        case 'canvas':
            return '🎨';
        case 'txt':
            return '📝';
        default:
            return '📄';
    }
}

// Special folder icons based on name
export function getFolderIcon(name) {
    const lower = name.toLowerCase();

    switch (lower) {
        case 'chapters':
            return '📚';
        case 'characters':
            return '👥';
        case 'world building':
        case 'worldbuilding':
        case 'world':
            return '🌍';
        case 'notes':
            return '📋';
        case 'research':
            return '🔍';
        case 'drafts':
            return '✏️';
        case 'trash':
            return '🗑️';
        default:
            return '📁';
    }
}

// Validate file/folder name
export function isValidName(name) {
    if (!name || name.trim() === '') return false;

    // Check for invalid characters
    const invalidChars = /[<>:"/\\|?*\x00-\x1f]/;
    if (invalidChars.test(name)) return false;

    // Check for reserved names
    const reserved = ['.', '..', 'con', 'prn', 'aux', 'nul'];
    if (reserved.includes(name.toLowerCase())) return false;

    return true;
}

// Generate unique name if file exists
export function generateUniqueName(existingNames, baseName, extension = '') {
    let name = extension ? `${baseName}.${extension}` : baseName;
    let counter = 1;

    while (existingNames.includes(name)) {
        name = extension
            ? `${baseName} (${counter}).${extension}`
            : `${baseName} (${counter})`;
        counter++;
    }

    return name;
}

// Sort files: folders first, then alphabetically
export function sortFiles(files) {
    return [...files].sort((a, b) => {
        if (a.type !== b.type) {
            return a.type === 'folder' ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
    });
}

// Create default project structure
export function getDefaultProjectStructure(projectName) {
    return getSharedDefaultProjectStructure(projectName);
}

export default {
    getExtension,
    getBaseName,
    getParentPath,
    getFileName,
    isMarkdownFile,
    isCanvasFile,
    getFileIcon,
    getFolderIcon,
    isValidName,
    generateUniqueName,
    sortFiles,
    getDefaultProjectStructure
};
